import { IGridItem, Item, ValidationModes, ValidationLevel } from "constants/product-collection";
import { List, Map } from "immutable";

import { amphibiousGetIn } from "helpers/objUtils";
import { ValidationResultWithIdentifier } from "./field_validation";
import { MathJsStatic } from "mathjs";
import { getValueGivenItemAndField, mathJsSymbolIsField, RESERVED_KEY, ScopeKeys } from "helpers/product-collection";

export const FORMULA_FIELD = "formula";
export const ROW_RULE = "row";
export const COLLECTION_RULE = "collection";
export const validateItemsWithRowValidations = (
    items: IGridItem[],
    validationProperties: List<Map<string, any>> = List(),
    mathJSInstance: Partial<MathJsStatic>,
    validationMode?: ValidationModes,
    context?: any
): ValidationResultWithIdentifier[] => {
    const validatedItems = [];
    if (validationProperties?.size) {
        const formulasAndDependencies = validationProperties
            .map((formulaObject) => {
                if (validationMode !== formulaObject.get("context")) {
                    return;
                }
                const originalFormula = formulaObject.get("formula");
                const parsedFormula = mathJSInstance.parse(originalFormula);
                const symbols = [];
                parsedFormula.traverse((node) => mathJsSymbolIsField(node, mathJSInstance) && symbols.push(node.name));
                return {
                    formula: parsedFormula.compile(),
                    symbols,
                    originalFormula,
                    level: formulaObject.get("level"),
                };
            })
            .filter((f) => !!f);
        items.forEach((item) => {
            const validationOutput: ValidationResultWithIdentifier = {
                id: amphibiousGetIn(item, Item.FIELDS.ID),
                errors: [],
                warnings: [],
            };
            const scope = { [RESERVED_KEY + ScopeKeys.Data]: item, [RESERVED_KEY + ScopeKeys.Context]: context };
            formulasAndDependencies.forEach(({ formula, symbols, originalFormula, level }) => {
                symbols.forEach((symbol) => (scope[symbol] = getValueGivenItemAndField(item.fields, symbol)));
                !formula.evaluate(scope) &&
                    (level === ValidationLevel.ERROR ? validationOutput.errors : validationOutput.warnings).push({
                        field: FORMULA_FIELD,
                        value: originalFormula,
                        rule: ROW_RULE,
                    });
            });
            validatedItems.push(validationOutput);
        });
    }

    return validatedItems;
};

export interface IValidationCollectionData {
    id: string;
    name: string;
}
export const validateCollectionProperties = (
    collectionData: IValidationCollectionData,
    validationProperties: List<Map<string, any>> = List(),
    mathJSInstance: Partial<MathJsStatic>,
    context?: any
): ValidationResultWithIdentifier[] => {
    const validationResults = [];
    if (validationProperties?.size) {
        const formulasAndDependencies = validationProperties
            .map((formulaObject) => {
                // Validating Collection must have `GLOBAL` validation context.
                if (formulaObject.get("context") !== ValidationModes.Global) {
                    return;
                }
                const originalFormula = formulaObject.get("formula");
                const parsedFormula = mathJSInstance.parse(originalFormula);
                const symbols = [];
                parsedFormula.traverse((node) => {
                    return mathJsSymbolIsField(node, mathJSInstance) && symbols.push(node.name);
                });
                return {
                    formula: parsedFormula.compile(),
                    symbols,
                    originalFormula,
                    level: formulaObject.get("level"),
                };
            })
            .filter((f) => !!f);

        const validationOutput: ValidationResultWithIdentifier = { id: "collection", errors: [], warnings: [] };
        const scope = { collection: collectionData, [RESERVED_KEY + ScopeKeys.Context]: context };
        formulasAndDependencies.forEach(({ formula, symbols, originalFormula, level }) => {
            !formula.evaluate(scope) &&
                (level === ValidationLevel.ERROR ? validationOutput.errors : validationOutput.warnings).push({
                    field: FORMULA_FIELD,
                    value: originalFormula,
                    rule: COLLECTION_RULE,
                });
        });
        validationResults.push(validationOutput);
    }

    return validationResults;
};
